import { MocoLayer } from "mobility-toolbox-js/ol";
import { ServiceConditionEnumeration } from "mobility-toolbox-js/types";
import { unByKey } from "ol/Observable";
import { memo, useEffect, useMemo, useRef } from "react";

import config from "../config";
import { NOTIFICATIONS_SOURCE_ID } from "../constants";
import {
  setAreSituationsOpen,
  setNotificationsLayer,
  setSituations,
} from "../model/actions";
import useAppDispatch from "../utils/hooks/useAppDispatch";
import useAppSelector from "../utils/hooks/useAppSelector";

import type { Feature } from "ol";
import type { Geometry } from "ol/geom";

import type { Situation } from "../model/reducers";

function NotificationsLayer() {
  const dispatch = useAppDispatch();
  const map = useAppSelector((state) => state.map);
  const maplibreLayer = useAppSelector((state) => state.backgroundLayer);
  const previewSituations = useAppSelector((state) => state.previewSituations);
  const situations = useAppSelector((state) => state.situations);
  const stationsLayer = useAppSelector((state) => state.stationsLayer);
  const situationsRef = useRef(situations);

  useEffect(() => {
    situationsRef.current = situations;
  }, [situations]);

  const layer = useMemo(() => {
    const mocoLayer = new MocoLayer({
      apiKey: config.notificationApiKey || config.apiKey,
      apiParameters: {
        hasGeoms: undefined, // Get all situations with geoms and without geoms
      },
      layersFilter: ({ source }: { source: string }) =>
        source === NOTIFICATIONS_SOURCE_ID,
      loadAll: !previewSituations,
      maplibreLayer: maplibreLayer ?? undefined,
      situations: previewSituations,
      tenant: config.notificationTenant || config.tenant,
      url: config.notificationApiUrl,
    });
    return mocoLayer;
  }, [maplibreLayer, previewSituations]);

  useEffect(() => {
    if (previewSituations) {
      dispatch(setSituations(previewSituations));
    }
  }, [dispatch, previewSituations]);

  useEffect(() => {
    // @ts-expect-error - bad typing for the "change:situations" event
    const key = layer.on("change:situations", (e: { target: MocoLayer }) => {
      dispatch(setSituations((e.target.situations ?? []) as Situation[]));
    });
    // Reload situations avery 5 minute
    const interval = setInterval(
      () => {
        void layer.updateData();
      },
      5 * 60 * 1000,
    ); // 5 minutes
    dispatch(setNotificationsLayer(layer));

    return () => {
      clearInterval(interval);
      unByKey(key);
    };
  }, [dispatch, layer, previewSituations]);

  // Opens the situations panel if there are new situations
  useEffect(() => {
    if ((situationsRef.current?.length ?? 0) < (situations?.length ?? 0)) {
      if (
        (situationsRef.current?.map(({ id }) => id).join(",") ?? "") !==
        (situations?.map(({ id }) => id).join(",") ?? "")
      ) {
        dispatch(setAreSituationsOpen(true));
      }
    }
  }, [situations, dispatch]);

  // Get the list of stations UICs that have notifications
  const stationsUics: null | string[] = useMemo(() => {
    const names =
      situations?.flatMap(({ publications }) => {
        return publications
          ?.filter((publication) => {
            return (
              publication.serviceCondition ===
              ServiceConditionEnumeration.DiscontinuedOperation
            );
          })
          ?.flatMap(({ publicationStops }) => {
            return publicationStops?.map((s) => s.name);
          });
      }) ?? [];

    return (
      (
        (stationsLayer?.getSource()?.getFeatures() ?? []) as Feature<
          Geometry,
          { name: string }
        >[]
      )
        .filter((s) => !!names?.includes(s?.get("name") as string))
        .map((s) => s.get("uic") as string) || []
    );
  }, [situations, stationsLayer]);

  // Highlight the stations with notifications
  useEffect(() => {
    // console.log("Set stationsUics for notif layer", stationsUics);
    const mbMap = maplibreLayer?.mbMap;
    if (mbMap) {
      mbMap.getStyle()?.layers.forEach((layerr) => {
        if (
          (layerr.metadata as Record<string, unknown> | undefined)?.[
            "tralis.moco"
          ] === "moco_station"
        ) {
          // @ts-expect-error - not able to fix it
          const filter = layerr.filter as string[][];
          if (filter?.[1]?.[2]) {
            // @ts-expect-error - not able to fix it
            filter[1][2] = stationsUics?.length ? stationsUics : [0]; // If no stations, filter to an impossible value
            // console.log("Filter for layer", layerr.id, filter);
            // @ts-expect-error - not able to fix it
            mbMap.setFilter(layerr.id, filter);
          } else {
            console.warn("Unexpected filter structure for layer", layerr.id);
          }
        }
      });
    }
    // For debugging
    // window.map = maplibreLayer?.mbMap;
  }, [maplibreLayer?.mbMap, stationsUics]);

  useEffect(() => {
    map?.addLayer(layer);
    return () => {
      map?.removeLayer(layer);
    };
  }, [layer, map]);

  return null;
}

export default memo(NotificationsLayer);
